/**
 * 🌧️ [Phase 56.5 — 2026-07-09] Dialog "Rallongement BT lors insertion jour-de-pluie"
 * extrait depuis CalendrierProduction.jsx (bloc ~L2087-2130 legacy).
 *
 * Rôle : modal de confirmation quand l'utilisatrice insère un BT sur une date
 * déjà cédulée pour d'autres BT du même chef d'équipe. Propose deux options :
 *   - Insérer sans rallonger (les BT existants sont décalés d'un jour)
 *   - Rallonger les BT à la fin (les BT existants gagnent un jour supplémentaire)
 *
 * Contrat : le parent passe `data` = payload `pluieRallongementDialog` OU `null`.
 * Le parent gère les callbacks `onCancel` (fermer sans agir) et
 * `onConfirm(cascade: boolean)` qui appelle `confirmPluieInsertion(cascade)`.
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Cloud } from 'lucide-react';

export default function PluieRallongementDialog({ data, onCancel, onConfirm }) {
  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent className="max-w-md" data-testid="dialog-pluie-rallongement">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-600" /> Conflit de date détecté
          </DialogTitle>
          <DialogDescription>
            Le {data.date} est déjà cédulé pour {data.btsARallonger.length} bon(s) de travail du même chef d'équipe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <p className="text-sm text-slate-700">Souhaitez-vous <b>rallonger</b> automatiquement ces BT d'une journée à la fin (la date sera retirée de leur planification) ?</p>
          <ul className="text-xs space-y-1 mt-2">
            {data.btsARallonger.map(bt => (
              <li key={bt.bon_id} className="flex justify-between bg-slate-50 px-2 py-1 rounded">
                <span className="font-medium">{bt.dossier_nom}</span>
                <span className="text-slate-500">{bt.nb_jours_actuel}j → {bt.nb_jours_actuel}j (1 jour décalé)</span>
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onCancel?.()} data-testid="btn-pluie-cancel">Annuler</Button>
          <Button
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => onConfirm?.(false)}
            data-testid="btn-pluie-no-cascade"
          >
            Insérer sans rallonger
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => onConfirm?.(true)}
            data-testid="btn-pluie-yes-cascade"
          >
            ✓ Rallonger les BT
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
